import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddInstitutionModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'hospital',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    licenseNumber: '',
    accreditation: '',
    specializations: [],
    activeUsers: 0,
    maxUsers: 100,
    subscriptionTier: 'institutional'
  });

  const [errors, setErrors] = useState({});

  const institutionTypes = [
    { value: 'hospital', label: 'Hospital' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'imaging_center', label: 'Imaging Center' },
    { value: 'diagnostic_lab', label: 'Diagnostic Laboratory' },
    { value: 'research_facility', label: 'Research Facility' },
    { value: 'academic', label: 'Academic Institution' }
  ];

  const subscriptionTiers = [
    { value: 'institutional', label: 'Institutional' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'academic', label: 'Academic' }
  ];

  const specializationOptions = [
    'Cardiology',
    'Neurology',
    'Oncology',
    'Orthopedics',
    'Pediatrics',
    'Radiology',
    'Emergency Medicine',
    'Internal Medicine'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSpecializationToggle = (specialization) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev?.specializations?.includes(specialization)
        ? prev?.specializations?.filter(s => s !== specialization)
        : [...prev?.specializations, specialization]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Institution name is required';
    }

    if (!formData?.contactEmail?.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    if (!formData?.contactPhone?.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    }

    if (!formData?.address?.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.state?.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData?.zipCode?.trim()) {
      newErrors.zipCode = 'Zip code is required';
    }

    if (!formData?.licenseNumber?.trim()) {
      newErrors.licenseNumber = 'License number is required';
    }

    if (formData?.specializations?.length === 0) {
      newErrors.specializations = 'Please select at least one specialization';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Building2" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Add New Institution</h2>
              <p className="text-sm text-muted-foreground">Register a new institutional account</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-clinical"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Institution Name *
                </label>
                <Input
                  name="name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  placeholder="Enter institution name"
                  error={errors?.name}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Institution Type *
                </label>
                <Select
                  options={institutionTypes}
                  value={formData?.type}
                  onChange={(value) => handleSelectChange('type', value)}
                  placeholder="Select type"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contact Email *
                </label>
                <Input
                  name="contactEmail"
                  type="email"
                  value={formData?.contactEmail}
                  onChange={handleInputChange}
                  placeholder="contact@institution.com"
                  error={errors?.contactEmail}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contact Phone *
                </label>
                <Input
                  name="contactPhone"
                  type="tel"
                  value={formData?.contactPhone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  error={errors?.contactPhone}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Address *
              </label>
              <Input
                name="address"
                value={formData?.address}
                onChange={handleInputChange}
                placeholder="Street address"
                error={errors?.address}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  City *
                </label>
                <Input
                  name="city"
                  value={formData?.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  error={errors?.city}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  State *
                </label>
                <Input
                  name="state"
                  value={formData?.state}
                  onChange={handleInputChange}
                  placeholder="State"
                  error={errors?.state}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Zip Code *
                </label>
                <Input
                  name="zipCode"
                  value={formData?.zipCode}
                  onChange={handleInputChange}
                  placeholder="12345"
                  error={errors?.zipCode}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  License Number *
                </label>
                <Input
                  name="licenseNumber"
                  value={formData?.licenseNumber}
                  onChange={handleInputChange}
                  placeholder="Enter license number"
                  error={errors?.licenseNumber}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Accreditation
                </label>
                <Input
                  name="accreditation"
                  value={formData?.accreditation}
                  onChange={handleInputChange}
                  placeholder="e.g., JCAHO, CAP"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Specializations *
              </label>
              <div className="flex flex-wrap gap-2">
                {specializationOptions?.map((spec) => (
                  <button
                    key={spec}
                    type="button"
                    onClick={() => handleSpecializationToggle(spec)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-clinical ${
                      formData?.specializations?.includes(spec)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
              {errors?.specializations && (
                <p className="text-error text-sm mt-2">{errors?.specializations}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Max Users
                </label>
                <Input
                  name="maxUsers"
                  type="number"
                  value={formData?.maxUsers}
                  onChange={handleInputChange}
                  placeholder="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Subscription Tier
                </label>
                <Select
                  options={subscriptionTiers}
                  value={formData?.subscriptionTier}
                  onChange={(value) => handleSelectChange('subscriptionTier', value)}
                  placeholder="Select tier"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            type="button"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={handleSubmit}
          >
            Add Institution
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddInstitutionModal;